import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { environment } from 'environments/environment';
import { BehaviorSubject, map, Observable, of, take, tap } from 'rxjs';

export interface ContentSearchResult {
    fileName: string;
    pageIndex: number;
    found: boolean;
    searchTerm: string;
}

@Injectable({ providedIn: 'root' })
export class DocumentContentSearchService {
    // Holds the full document’s indexed search results
    private index$ = new BehaviorSubject<ContentSearchResult[]>([]);
    private loadedDocId: string | null = null;

    private apiUrl = environment.apiRoot;

    constructor(private http: HttpClient, private appSettingsStore: AppSettingsStore) {}

    /**
     * Fetch and cache the entire document’s search data.
     * Only calls HTTP if docId changed or we have no data yet.
     */
    loadDocumentContent(
        docId: string | number,
        clientName: string,
        userId: string
    ): Observable<ContentSearchResult[]> {
        const normalizedDocId = String(docId);

        if (this.loadedDocId === normalizedDocId && this.index$.value.length) {
            // already loaded → just replay
            return of(this.index$.value);
        }

        const headers = new HttpHeaders().set('X-ClientId', clientName).set('X-User', userId);

        // build query param for clientId (per your controller)
        const params = new HttpParams().set('clientId', clientName);

        return this.http
            .get<any>(`${this.apiUrl}documents/${normalizedDocId}/json/raw`, { headers, params })
            .pipe(
                map((raw) => this.parseRawOCR(raw)),
                tap((results) => {
                    this.index$.next(results);
                    this.loadedDocId = normalizedDocId;
                })
            );
    }

    /**
     * Filter the in-memory index by the given term.
     * Returns an Observable of the filtered array.
     */
    filter(term: string): Observable<ContentSearchResult[]> {
        const t = term.trim().toLowerCase();
        if (!t) return of([]);

        return this.index$.pipe(
            take(1),
            map((arr) => arr.filter((r) => r.found && r.searchTerm.toLowerCase().includes(t)))
        );
    }

    /** Optional: clear cache if you ever need to force-reload */
    clearCache(): void {
        this.index$.next([]);
        this.loadedDocId = null;
    }

    /**
     * Helper: traverse pages→blocks→lines→words to build SearchResult[]
     */
    private parseRawOCR(raw: any): ContentSearchResult[] {
        const results: ContentSearchResult[] = [];
        if (!raw.pages || !Array.isArray(raw.pages)) {
            return results;
        }
        for (const page of raw.pages) {
            const pageIndex = page.page_idx ?? 0;
            if (!page.blocks || !Array.isArray(page.blocks)) continue;

            for (const block of page.blocks) {
                if (!block.lines || !Array.isArray(block.lines)) continue;

                for (const line of block.lines) {
                    if (!line.words || !Array.isArray(line.words)) continue;

                    for (const word of line.words) {
                        if (word.value) {
                            results.push({
                                fileName: '', // or populate if your JSON provides a filename
                                pageIndex,
                                found: true,
                                searchTerm: word.value,
                            });
                        }
                    }
                }
            }
        }
        return results;
    }
}
